import React, { useState } from 'react';
import I18n from 'i18n-js';
import { compact } from 'lodash';

import { RCButton, RCMenu } from '@reverbdotcom/cadence/components';
import {
  PencilSimpleIcon,
  MapPinLineIcon,
  TrendUpIcon,
  HandshakeIcon,
  TagIcon,
  BucksIcon,
} from '@reverbdotcom/cadence/icons/react';
import { ClockCounterClockwiseIcon } from '@phosphor-icons/react';

import { gql } from '@reverbdotcom/commons/src/gql';
import { useExpEnabled, useUser } from '@reverbdotcom/commons/src/user_hooks';
import experiments from '@reverbdotcom/commons/src/experiments';
import { useQuery } from '@reverbdotcom/commons/src/useQuery';
import {
  Listing,
  SellerListingCardPaymentSettingsQuery,
  CoreMyListingAvailableSalesMemberships,
  core_apimessages_Listing_SeedType as SeedType,
} from '@reverbdotcom/commons/src/gql/graphql';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import SellerListingsCollectionContext from '../listings_management/SellerListingsCollectionContext';

import URLHelpers, { Paths } from '../shared/url_helpers';
import Location from '../../lib/wrapped_location';
import BumpModalV2WithController from './footer_buttons/BumpButtonV2';
import { bumpRatePercentToDecimal } from '../bump/bump_helpers';
import { AutoOfferModalV2 } from './footer_buttons/AutoOfferModalButtonV2';
import SalesModal, { AVAILABLE_SALES_MEMBERSHIPS_QUERY } from './footer_buttons/sales/SalesModal';
import { AffirmZeroPercentModal } from './footer_buttons/affirm_zero_percent_financing/AffirmZeroPercentButton';
import { SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY } from './footer_buttons/PaymentButton';
import SellerListingCardPaymentSettingsModal from './SellerListingCardPaymentSettingsModal';
import { EndModal } from './footer_buttons/EndButton';
import { canViewListingPaymentSetting, DeleteDraftListingErrors, SELLER_SITE } from './listingActionMenuHelpers';
import { DRAFT, LIVE, ORDERED } from './toolbarHelpers';
import DeleteButton from './footer_buttons/DeleteButton';
import PublishButton from './footer_buttons/PublishButton';
import RelistButton from './footer_buttons/RelistButton';
import RenewListingModal from '../listings/RenewListingModal';

export const SellerListingCardTradeInFragment = gql(`
  fragment SellerListingCardTradeInFooter on Listing {
    _id
    id
    tradeInEligibility {
      isEligible
    }
    csp {
      _id
      id
    }
  }
`);

interface Props {
  listing: Listing;
  hasRecommendation: boolean;
}

export default function SellerListingCardInsightsActionMenu({ listing, hasRecommendation }: Props) {
  const [showBumpModal, setShowBumpModal] = useState(false);
  const [showSalesModal, setShowSalesModal] = useState(false);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showAutoOfferModal, setShowAutoOfferModal] = useState(false);
  const [showAffirmZeroPercentModal, setShowAffirmZeroPercentModal] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [isEndModalOpen, setIsEndModalOpen] = useState(false);
  const [draftDeleteErrors, setDraftDeleteErrors] = useState<Array<string>>([]);
  const {
    useLazySellerListingsQuery,
  } = React.useContext(SellerListingsCollectionContext);

  const { refetchSellerListings } = useLazySellerListingsQuery();
  const user = useUser();
  const isListingLive = listing.state === LIVE;
  const isListingDraft = listing.state === DRAFT;
  const isSellerSiteExclusive = listing.exclusiveChannel == SELLER_SITE;
  const isEditable = listing.state !== ORDERED || listing.relistable;

  const bumpRate = listing.bumpRate?.rate || 0;
  const isBumpEligible = isListingLive && !isSellerSiteExclusive;
  const isAutoOffersEnabled = listing.autoOffers.id !== '';
  const showSaleButton = isListingLive && !isSellerSiteExclusive && listing.shop.canCurateSales;
  const showAffirmZeroPercentButton = (listing.affirmZeroPercentFinancingConfiguration.isEligible || listing.affirmZeroPercentFinancingConfiguration.isEnabled) && isListingLive;
  const isAffirmEnabled = listing.affirmZeroPercentFinancingConfiguration.isEnabled;
  const showTradeIn = isListingLive && listing.tradeInEligibility?.isEligible && listing.csp?.id;
  const renewEligible = Boolean(listing.renewalEligibility?.isEligible);

  const hasAttractBuyersItems = (showTradeIn ||
    isBumpEligible ||
    listing.isAutoOfferEligible ||
    showSaleButton ||
    showAffirmZeroPercentButton ||
    renewEligible);

  const { data: paymentData, loading: paymentLoading } = useQuery<SellerListingCardPaymentSettingsQuery>(SELLER_LISTING_CARD_PAYMENT_SETTINGS_QUERY);
  const { myDirectCheckoutProfile } = paymentData || {};
  const walletOptInEnabled = useExpEnabled(experiments.LISTING_LEVEL_WALLET_OPT_IN);
  const showPaymentButton = isListingLive && !paymentLoading && canViewListingPaymentSetting(user, myDirectCheckoutProfile, walletOptInEnabled);

  const {
    data: salesData,
    loading: salesLoading,
  } = useQuery<CoreMyListingAvailableSalesMemberships.Query>(AVAILABLE_SALES_MEMBERSHIPS_QUERY, {
    skip: !showSaleButton,
    ssr: false,
    fetchPolicy: 'network-only',
    variables: {
      listingId: listing.id,
    },
    errorPolicy: 'all',
  });

  const availableSalesMemberships = salesData?.listing?.availableSalesMemberships;
  const reverbSalesMemberships = compact(availableSalesMemberships?.reverbSalesMemberships);
  const sellerSalesMemberships = compact(availableSalesMemberships?.sellerSalesMemberships);
  const inSale = [...reverbSalesMemberships, ...sellerSalesMemberships].some(sale => sale.isListingInSale);
  const salesButtonText = (salesLoading || inSale) ? I18n.t('discovery.listingsManagement.sellerListingCard.sales.manageSales') : I18n.t('discovery.listingsManagement.sellerListingCard.sales.addToSales');

  const sellFormEditListingPath = URLHelpers.sellFormEditListingPath(listing.id, {
    section: 'review',
  });

  const tradeInPath = showTradeIn
    ? Paths.sellerTradeIn.expand({ seed_id: listing.id, seed_type: SeedType.Product })
    : null;
  const buttonVariant = hasRecommendation ? 'transparent' : 'outlined';
  const buttonSize = hasRecommendation ? 'mini' : 'small';

  return (
    <>
      <RCMenu
        anchor={
          <RCButton
            variant={buttonVariant}
            size={buttonSize}
            trailingIcon="dropdown"
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.moreListingActions')}
          </RCButton>
        }
        position="bottom-end"
      >
        {isEditable && (
          <RCMenu.Item
            icon={<PencilSimpleIcon />}
            onClick={() => {
              trackEvent({
                eventName: MParticleEventName.ClickedComponent,
                componentName: 'EditButton',
                listingId: listing.id,
              });
              Location.assign(sellFormEditListingPath);
            }}
          >
            {I18n.t('discovery.listingsManagement.sellerListingCard.edit')}
          </RCMenu.Item>
        )}
        {isListingDraft && (
          <PublishButton listing={listing} insightsVariant />
        )}
        {listing.relistable && (
          <RelistButton listing={listing} />
        )}
        {hasAttractBuyersItems && (
          <>
            <RCMenu.Separator />
            <RCMenu.Group label={I18n.t('discovery.listingsManagement.sellerListingCard.attractBuyers')}>
              {renewEligible && (
                <RCMenu.Item
                  icon={<ClockCounterClockwiseIcon />}
                  onClick={() => {
                    setShowRenewModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'RenewButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {I18n.t('discovery.listingsManagement.renewListing.button')}
                </RCMenu.Item>
              )
              }
              {showTradeIn && (
                <RCMenu.Item
                  icon={<MapPinLineIcon />}
                  onClick={() => Location.assign(tradeInPath)}
                >
                  {I18n.t('discovery.tradeIn.sellerListingCardCallout')}
                </RCMenu.Item>
              )}
              {isBumpEligible && (
                <RCMenu.Item
                  icon={<TrendUpIcon />}
                  onClick={() => {
                    setShowBumpModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'BumpButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {listing.bumpRate?.rate ? I18n.t('discovery.bumpButton.manageBump') : I18n.t('discovery.listingsManagement.sellerListingCard.promoteWithBump')}
                </RCMenu.Item>
              )}
              {listing.isAutoOfferEligible && (
                <RCMenu.Item
                  icon={<HandshakeIcon />}
                  onClick={() => {
                    setShowAutoOfferModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'AutoOfferModalButtonV2',
                      listingId: listing.id,
                    });
                  }}
                >
                  {isAutoOffersEnabled
                    ? I18n.t('discovery.listingsManagement.sellerListingCard.offers.autoOffers.autoOffersManage')
                    : I18n.t('discovery.listingsManagement.sellerListingCard.offers.autoOffers.autoOffersEnable')}
                </RCMenu.Item>
              )}
              {showSaleButton && (
                <RCMenu.Item
                  icon={<TagIcon />}
                  onClick={() => {
                    setShowSalesModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedComponent,
                      componentName: 'SalesModalButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {salesButtonText}
                </RCMenu.Item>
              )}
              {showAffirmZeroPercentButton && (
                <RCMenu.Item
                  icon={<BucksIcon />}
                  onClick={() => {
                    setShowAffirmZeroPercentModal(true);
                    trackEvent({
                      eventName: MParticleEventName.ClickedAffirmZeroPercentFinancingButton,
                      componentName: 'AffirmZeroPercentButton',
                      listingId: listing.id,
                    });
                  }}
                >
                  {isAffirmEnabled
                    ? I18n.t('discovery.listingsManagement.sellerListingCard.affirmZeroPercent.disableButtonText')
                    : I18n.t('discovery.listingsManagement.sellerListingCard.affirmZeroPercent.enableButtonText')}
                </RCMenu.Item>
              )}
            </RCMenu.Group>
          </>
        )}

        {showPaymentButton && (
          <>
            <RCMenu.Separator />
            <RCMenu.Item
              onClick={() => {
                trackEvent({
                  eventName: MParticleEventName.ClickedComponent,
                  componentName: 'PaymentButton',
                  listingId: listing.id,
                });
                setIsPaymentModalOpen(true);
              }}
            >
              {I18n.t('discovery.listingsManagement.sellerListingCard.payment')}
            </RCMenu.Item>
          </>
        )}
        {isListingLive && (
          <>
            <RCMenu.Separator />
            <RCMenu.Item
              destructive
              onClick={() => {
                trackEvent({
                  eventName: MParticleEventName.ClickedComponent,
                  componentName: 'EndButton',
                  listingId: listing.id,
                });
                setIsEndModalOpen(true);
              }}
            >
              {I18n.t('discovery.listingsManagement.sellerListingCard.end')}
            </RCMenu.Item>
          </>
        )}
        {isListingDraft && (
          <>
            <RCMenu.Separator />
            <DeleteButton listing={listing} setDraftDeleteErrors={setDraftDeleteErrors} insightsVariant />
          </>
        )}
      </RCMenu>

      <BumpModalV2WithController
        listingId={listing.id}
        bid={bumpRatePercentToDecimal(bumpRate)}
        showModal={showBumpModal}
        setShowModal={setShowBumpModal}
        listing={listing}
      />
      <SalesModal
        data={salesData}
        isModalOpen={showSalesModal}
        onModalClose={() => setShowSalesModal(false)}
      />
      <AutoOfferModalV2
        showModal={showAutoOfferModal}
        setShowModal={setShowAutoOfferModal}
        listingId={listing.id}
      />
      <AffirmZeroPercentModal
        isAffirmEnabled={isAffirmEnabled}
        listingId={listing.id}
        showModal={showAffirmZeroPercentModal}
        setShowModal={setShowAffirmZeroPercentModal}
      />
      {showPaymentButton && (
        <SellerListingCardPaymentSettingsModal
          listing={listing}
          isModalOpen={isPaymentModalOpen}
          setIsModalOpen={setIsPaymentModalOpen}
        />
      )}
      {isListingLive && (
        <EndModal
          listing={listing}
          modalOpen={isEndModalOpen}
          setModalOpen={setIsEndModalOpen}
        />
      )}
      {draftDeleteErrors.length > 0 && (
        <DeleteDraftListingErrors
          draftDeleteErrors={draftDeleteErrors}
          setDraftDeleteErrors={setDraftDeleteErrors}
        />
      )}
      <RenewListingModal
        isOpen={showRenewModal}
        listing={listing}
        requestClose={() => setShowRenewModal(false)}
        onSuccessCallback={() => {
          setShowRenewModal(false);
          refetchSellerListings([listing.id]);
        }}
      />
    </>
  );
}
